import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { EnhancedHeader } from '@/components/common/EnhancedHeader';
import { MessageCircle, HeadphonesIcon } from 'lucide-react';

const ChatPage: React.FC = () => {
  const { tailorId, tailorName } = useParams<{ tailorId?: string; tailorName?: string }>();
  const { user } = useAuth();
  const [selectedChat, setSelectedChat] = useState<'helpline' | 'tailor'>('helpline');

  const helplineTailor = {
    id: 0,
    name: 'MasterJi Support',
    email: 'support@masterji.com',
    role: 'admin'
  };

  const regularTailor = tailorId ? {
    id: parseInt(tailorId),
    name: tailorName || 'Tailor',
    email: '',
    role: 'tailor'
  } : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <EnhancedHeader title="Messaging" showBackButton backTo="/customer/dashboard" />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Chat Selection */}
        <div className="mb-6">
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setSelectedChat('helpline')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                selectedChat === 'helpline'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <HeadphonesIcon className="w-5 h-5" />
              <span>Customer Support</span>
            </button>
            
            {regularTailor && (
              <button
                onClick={() => setSelectedChat('tailor')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedChat === 'tailor'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <MessageCircle className="w-5 h-5" />
                <span>{regularTailor.name}</span>
              </button>
            )}
          </div>
          
          {selectedChat === 'helpline' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Customer Support Chat
              </h3>
              <p className="text-blue-700 dark:text-blue-200 text-sm">
                Get help with your orders, account issues, or any questions. Our support team is here to assist you.
              </p>
            </div>
          )}
        </div>
        
        {/* Chat Interface */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg">
          {selectedChat === 'helpline' && user ? (
            <div className="p-6">
              <div className="text-center py-12">
                <HeadphonesIcon className="w-16 h-16 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
                  Customer Support Chat
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-6">
                  Our support team will respond to your messages within 24 hours.
                </p>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">
                    <strong>What can we help you with?</strong>
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                    <li>• Order status and tracking</li>
                    <li>• Account and billing questions</li>
                    <li>• Technical support</li>
                    <li>• General inquiries</li>
                  </ul>
                </div>
                <button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Start Support Chat
                </button>
              </div>
            </div>
          ) : regularTailor && user ? (
            <div className="p-6">
              <div className="text-center py-12">
                <MessageCircle className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
                  Chat with {regularTailor.name}
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-6">
                  Communicate directly with your assigned tailor about your orders.
                </p>
                <button className="mt-6 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Start Chat
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-600 dark:text-slate-400">Please log in to start chatting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;